'use client'

// The vending machine's own panel, for visitors (28 Oct). Sam: "on the UI for users - can we show the users price, not
// the object price", "we also need to avoid layout shifts and be cleverer with the UI so it feels slicker, and more akin
// to a vending machine...this needs to feel impressive and fun", and B3 and B4 are his packs for them: "highlight the
// buttons, have a little info icon and when clicked, it can show that this is a random print for the buyer - maybe a
// little 'for you' pill".
//
// One frame of fixed size, so nothing moves as it is used: a dot-matrix screen that says what is picked (or why it
// cannot be, or what a pack is), sixteen backlit keys with what each costs them, one big button to buy, and a line for
// what waits in his tray. The till opens in the same frame. Each key clicks down with a beep.

import { useEffect, useMemo, useState } from 'react'
import { getCtx } from '../../lib/audio/context'
import { price, FOR_YOU, keysOf } from '../../lib/print-shop/vendKeys'

const IDLE_MS = 2600

// a key going down: a short square blip, quiet
function blip(high = false) {
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') ctx.resume().catch(() => {})
  const o = ctx.createOscillator()
  const g = ctx.createGain()
  o.type = 'square'
  o.frequency.value = high ? 1320 : 990
  const t = ctx.currentTime + 0.005
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(0.05, t + 0.005)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.07)
  o.connect(g); g.connect(ctx.destination)
  o.start(t); o.stop(t + 0.08)
}

function Key({ k, picked, onPick, onInfo }) {
  const on = picked === k.code
  const cls = ['vm-key', on && 'on', !k.can && 'off', k.forYou && 'you'].filter(Boolean).join(' ')
  return (
    <button className={cls} onClick={() => onPick(k)} aria-pressed={on} aria-label={`${k.code} ${k.label}${k.can ? `, ${price(k.pence)}` : `, ${k.why?.toLowerCase()}`}`}>
      <span className="vm-key-top">
        <span className="vm-code">{k.code}</span>
        {k.forYou ? <span className="vm-pill">FOR YOU</span> : <span className="vm-price">{k.pence ? price(k.pence) : ''}</span>}
      </span>
      <span className="vm-label">{k.label}</span>
      {k.forYou && (
        <span className="vm-foot-row">
          <span className="vm-price">{price(k.pence)}</span>
          <span className="vm-info" role="button" tabIndex={0} aria-label={`what is the ${k.label}?`} onClick={(e) => { e.stopPropagation(); onInfo(k) }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onInfo(k) } }}>i</span>
        </span>
      )}
      {!k.forYou && (k.stuck || k.soldOut) && <span className={`vm-stamp${k.stuck ? ' stuck' : ''}`}>{k.stuck ? 'STUCK' : 'SOLD OUT'}</span>}
    </button>
  )
}

export function VendingPanel({ data, busy, said, till, onBuy, onClose, onBack }) {
  const keys = useMemo(() => keysOf(data), [data])
  const [picked, setPicked] = useState(null)
  const [info, setInfo] = useState(null)
  const [idle, setIdle] = useState(0)
  const [flick, setFlick] = useState(0) // the screen flickers when what it says changes
  const k = keys.find((x) => x.code === picked) || null

  useEffect(() => {
    const t = setInterval(() => setIdle((i) => i + 1), IDLE_MS)
    return () => clearInterval(t)
  }, [])

  const pick = (key) => {
    blip(key.can)
    setInfo(null)
    setPicked((p) => (p === key.code ? null : key.code))
    setFlick((f) => f + 1)
  }
  const showInfo = (key) => {
    blip(true)
    setPicked(key.code)
    setInfo((i) => (i === key.code ? null : key.code))
    setFlick((f) => f + 1)
  }
  const buy = () => {
    if (!k?.can || busy) return
    blip(true)
    onBuy(k.forYou || 'gift', k.forYou ? null : k.code)
  }

  // what the screen says: two lines and a third when there is more to tell
  const tray = data?.tray || []
  const idleLines = [
    'SELECT A ROW',
    'BUY PINO A SNACK',
    'B3 AND B4 ARE FOR YOU',
    data?.opened ? 'THE LITTLE SCREEN SAYS OPEN' : 'THE KEYPAD TAKES A CODE',
  ]
  let screen
  if (!data) screen = ['LOOKING THROUGH THE GLASS', '', '']
  else if (!data.on) screen = ['THE MACHINE IS DARK', 'COME BACK LATER', '']
  else if (busy) screen = ['TO THE TILL...', k ? `${k.code} ${k.label.toUpperCase()}` : '', '']
  else if (said && !picked) screen = ['THE TILL JAMMED', said.toUpperCase().slice(0, 44), 'TRY AGAIN']
  else if (k && info === k.code && k.forYou) screen = [`${k.code} ${FOR_YOU[k.forYou].title} · ${price(k.pence)}`, ...FOR_YOU[k.forYou].lines]
  else if (k && !k.can) screen = [`${k.code} ${k.label.toUpperCase()}`, k.why || '', '']
  else if (k && k.forYou) screen = [`${k.code} ${FOR_YOU[k.forYou].title}`, `FOR YOU · ${price(k.pence)} · POSTED TO YOU`, 'PRESS i TO SEE WHAT IT IS']
  else if (k) screen = [`${k.code} ${k.label.toUpperCase()}`, `FOR PINO · ${price(k.pence)}`, 'INTO HIS TRAY, HALF TO HIS PURSE']
  else screen = [idleLines[idle % idleLines.length], '', '']

  const buyLabel = busy ? 'TO THE TILL...' : !k ? 'SELECT A ROW' : !k.can ? (k.why || 'NOT TODAY') : k.forYou ? `BUY FOR YOU · ${price(k.pence)}` : `BUY FOR PINO · ${price(k.pence)}`
  const footer = tray.length ? `IN THE TRAY FOR HIM: ${tray.map((t) => `${t.label} from ${t.from}`).join(', ')}` : 'THE TRAY IS EMPTY'

  return (
    <div className="vm-panel" role="dialog" aria-label="his vending machine">
      <style>{CSS}</style>
      <div className="vm-head">
        <span className="vm-brand">PINO&apos;S <b>VENDING</b></span>
        <button className="vm-x" onClick={till ? onBack : onClose} aria-label={till ? 'back to the machine' : 'close'}>{till ? '‹' : '×'}</button>
      </div>
      {till ? (
        <div className="vm-till">{till}</div>
      ) : (
        <>
          <div className="vm-lcd" aria-live="polite">
            <div key={flick} className="vm-lcd-in">
              <div className="vm-l1">{screen[0]}{!data || (!k && data?.on) ? <span className="vm-caret">_</span> : null}</div>
              <div className="vm-l2">{screen[1]}</div>
              <div className="vm-l2">{screen[2]}</div>
            </div>
          </div>
          <div className="vm-grid">
            {keys.length
              ? keys.map((x) => <Key key={x.code} k={x} picked={picked} onPick={pick} onInfo={showInfo} />)
              : Array.from({ length: 16 }, (_, i) => <div key={i} className="vm-key ghost" />)}
          </div>
          <button className={`vm-buy${k?.can && !busy ? ' ready' : ''}${k?.forYou ? ' you' : ''}`} disabled={!k?.can || !!busy} onClick={buy}>{buyLabel}</button>
          <div className="vm-foot" title={footer}>{footer}</div>
        </>
      )}
    </div>
  )
}

const CSS = `
.vm-panel { position: absolute; right: 24px; top: 50%; transform: translateY(-50%); width: 384px; height: min(568px, calc(100dvh - 32px));
  display: flex; flex-direction: column; gap: 10px; padding: 12px 12px 10px; box-sizing: border-box; pointer-events: auto;
  background: linear-gradient(180deg, #2a1416 0%, #1a0d0f 100%); border: 2px solid #000; border-radius: 16px;
  box-shadow: inset 0 0 0 2px #b3262f, inset 0 0 0 4px #1a0d0f, 0 18px 50px rgba(0,0,0,.55); color: #f6e9e9;
  font-family: var(--font-geist-mono), ui-monospace, Menlo, monospace; animation: vm-in .28s cubic-bezier(.2,1.1,.4,1) both; }
@keyframes vm-in { from { transform: translateY(-50%) translateX(30px); opacity: 0 } to { transform: translateY(-50%); opacity: 1 } }
@media (max-width: 640px) {
  .vm-panel { right: 12px; left: 12px; width: auto; top: auto; bottom: 12px; transform: none; animation-name: vm-up; }
  @keyframes vm-up { from { transform: translateY(30px); opacity: 0 } to { transform: none; opacity: 1 } }
}
.vm-head { display: flex; align-items: center; justify-content: space-between; height: 26px; flex: none; }
.vm-brand { font-size: 12px; letter-spacing: 3px; color: #f2b8bb; } .vm-brand b { color: #fff; }
.vm-x { width: 26px; height: 26px; border-radius: 50%; border: 1px solid #000; background: #3a1c1f; color: #f6e9e9; font-size: 16px; line-height: 1; cursor: pointer; }
.vm-x:hover { background: #b3262f; }
.vm-lcd { flex: none; height: 74px; border-radius: 8px; border: 2px solid #000; overflow: hidden; position: relative;
  background: radial-gradient(circle at 1px 1px, rgba(255,177,59,.08) 1px, transparent 1.2px) 0 0 / 3px 3px, #160b03;
  box-shadow: inset 0 2px 10px rgba(0,0,0,.8); padding: 9px 11px; box-sizing: border-box; color: #ffb13b; text-shadow: 0 0 6px rgba(255,160,40,.65); }
.vm-lcd::after { content: ''; position: absolute; inset: 0; background: repeating-linear-gradient(to bottom, rgba(0,0,0,.18) 0 1px, transparent 1px 3px); pointer-events: none; }
.vm-lcd-in { animation: vm-flick .18s steps(2) both; }
@keyframes vm-flick { 0% { opacity: .2 } 50% { opacity: .9 } 100% { opacity: 1 } }
.vm-l1 { font-size: 15px; font-weight: 700; letter-spacing: 1.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; height: 20px; }
.vm-l2 { font-size: 10.5px; letter-spacing: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; height: 15px; opacity: .92; }
.vm-caret { animation: vm-blink 1s steps(1) infinite; } @keyframes vm-blink { 50% { opacity: 0 } }
.vm-grid { flex: 1; min-height: 0; display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(4, 1fr); gap: 7px; }
.vm-key { position: relative; display: flex; flex-direction: column; justify-content: space-between; min-width: 0; padding: 6px 7px; box-sizing: border-box;
  border-radius: 9px; border: 1px solid #000; background: linear-gradient(180deg, #3b2326, #2a171a); color: #f6e9e9; text-align: left; cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 3px 0 #0d0607; font: inherit; overflow: hidden; transition: transform .06s, box-shadow .06s, background .12s; }
.vm-key:hover { background: linear-gradient(180deg, #48292d, #301a1d); }
.vm-key:active { transform: translateY(2px); box-shadow: inset 0 1px 0 rgba(255,255,255,.05), 0 1px 0 #0d0607; }
.vm-key.on { background: linear-gradient(180deg, #5a1d23, #3a1216); box-shadow: inset 0 0 0 2px #ff4d5a, 0 0 14px rgba(255,77,90,.45), 0 3px 0 #0d0607; }
.vm-key.off { opacity: .5; }
.vm-key.off.on { opacity: .8; }
.vm-key.you { background: linear-gradient(180deg, #2e2350, #1d1636); box-shadow: inset 0 0 0 1px #7c5cff, 0 0 12px rgba(124,92,255,.35), 0 3px 0 #0d0607; }
.vm-key.you.on { box-shadow: inset 0 0 0 2px #a78bfa, 0 0 18px rgba(124,92,255,.7), 0 3px 0 #0d0607; }
.vm-key.ghost { cursor: default; background: linear-gradient(90deg, #2a171a 0%, #3b2326 50%, #2a171a 100%) 0 0 / 200% 100%; animation: vm-shimmer 1.2s linear infinite; }
@keyframes vm-shimmer { to { background-position: -200% 0 } }
.vm-key-top { display: flex; align-items: center; justify-content: space-between; gap: 4px; }
.vm-code { font-weight: 800; font-size: 11px; color: #ffb13b; text-shadow: 0 0 5px rgba(255,160,40,.5); }
.vm-price { font-size: 10.5px; font-weight: 700; color: #fff; }
.vm-label { font-family: var(--font-rounded), ui-rounded, system-ui, sans-serif; font-size: 11px; line-height: 1.15; color: #f2dede;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.vm-pill { font-size: 8.5px; font-weight: 800; letter-spacing: .6px; padding: 2px 5px; border-radius: 99px; background: #7c5cff; color: #fff; white-space: nowrap; }
.vm-foot-row { display: flex; align-items: center; justify-content: space-between; }
.vm-info { width: 16px; height: 16px; border-radius: 50%; border: 1px solid #a78bfa; color: #d9ccff; font-size: 10px; font-weight: 800; font-style: italic;
  display: inline-flex; align-items: center; justify-content: center; font-family: Georgia, serif; }
.vm-info:hover { background: #7c5cff; color: #fff; }
.vm-stamp { position: absolute; right: -14px; bottom: 9px; transform: rotate(-24deg); background: #b3262f; color: #fff; font-size: 7.5px; font-weight: 800;
  letter-spacing: .8px; padding: 1px 16px; } .vm-stamp.stuck { background: #c77a12; }
.vm-buy { flex: none; height: 50px; border-radius: 12px; border: 2px solid #000; font: inherit; font-size: 14px; font-weight: 800; letter-spacing: 1.5px;
  background: #3a1c1f; color: #a98b8d; box-shadow: 0 4px 0 #0d0607; cursor: default; transition: transform .06s, box-shadow .06s; }
.vm-buy.ready { background: linear-gradient(180deg, #ff4d5a, #c8202d); color: #fff; cursor: pointer; animation: vm-pulse 1.6s ease-in-out infinite; }
.vm-buy.ready.you { background: linear-gradient(180deg, #9b80ff, #6a46f0); }
.vm-buy.ready:active { transform: translateY(3px); box-shadow: 0 1px 0 #0d0607; }
@keyframes vm-pulse { 50% { filter: brightness(1.12); box-shadow: 0 4px 0 #0d0607, 0 0 18px rgba(255,77,90,.45) } }
.vm-foot { flex: none; height: 18px; font-size: 10px; letter-spacing: .8px; color: #c9a7aa; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-transform: uppercase; }
.vm-till { flex: 1; min-height: 0; overflow-y: auto; border-radius: 10px; }
`
